import { useState, useEffect, type MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Edit3, Loader2, Package, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@client/src/utils/logger';
import { orders } from '@client/src/api';
import type { OrderRecord } from '@client/src/types/order';
import { Button } from '@client/src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@client/src/components/ui/alert-dialog';
import ImageComponent from '@client/src/components/ui/image';

const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgLoading, setImgLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const STORAGE_KEY_ACTIVE_PLATFORM = 'order:activePlatform';

  const fetchOrder = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data: OrderRecord = await orders.getOrderById(id);
      setOrder(data);
      try {
        window.localStorage.setItem(STORAGE_KEY_ACTIVE_PLATFORM, data.platform);
      } catch (err) {
        logger.warn('持久化 platform 失败', err);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '加载失败';
      setError(message);
      logger.error('订单详情加载失败', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await orders.deleteOrder(id);
      toast.success('删除成功');
      navigate('/');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '删除失败';
      logger.error('删除订单失败', err);
      toast.error('删除失败，请重试');
      setDeleting(false);
    }
  };

  const copyToClipboardFallback = (text: string): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';

    document.body.appendChild(textarea);

    const selection = document.getSelection();
    const selectedRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      logger.error('fallback 复制失败', err);
      success = false;
    }

    document.body.removeChild(textarea);

    if (selectedRange && selection) {
      selection.removeAllRanges();
      selection.addRange(selectedRange);
    }

    return success;
  };

  const handleCopy = async (trackingNumber: string) => {
    let ok = false;

    if (
      window.isSecureContext &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      try {
        await navigator.clipboard.writeText(trackingNumber);
        ok = true;
      } catch (err: unknown) {
        logger.warn('navigator.clipboard 复制失败，尝试降级方案', err);
      }
    }

    if (!ok) {
      ok = copyToClipboardFallback(trackingNumber);
    }

    if (ok) {
      toast.success('已复制到剪贴板');
    } else {
      logger.error('复制运单号失败：所有方案均无效');
      toast.error('复制失败，请手动长按复制');
    }
  };

  const capitalize = (s: string): string =>
    s.charAt(0).toUpperCase() + s.slice(1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={fetchOrder}>重试</Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">订单不存在</p>
      </div>
    );
  }

  const isShipped = order.shippingStatus === 'shipped';
  const isArrived = order.warehouseStatus === 'arrived';

  return (
    <div className="min-h-screen bg-background">
      {/* 顶部导航栏 */}
      <header
        className="sticky top-0 z-10 bg-card border-b h-[52px] flex items-center px-4"
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-md hover:bg-accent transition-colors"
          aria-label="返回"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h1 className="flex-1 text-center text-base font-medium text-foreground">
          订单详情
        </h1>
        <button
          type="button"
          onClick={() => navigate(`/orders/${id}/edit`)}
          className="p-2 -mr-2 rounded-md hover:bg-accent transition-colors text-foreground"
          aria-label="编辑"
        >
          <Edit3 className="w-5 h-5" />
        </button>
      </header>

      {/* 内容区 */}
      <main className="pt-4 pb-8 px-4 space-y-6 max-w-md mx-auto">
        {/* 订单图片区 */}
        <div className="w-full aspect-square rounded-lg overflow-hidden shadow-sm bg-accent relative">
          {imgLoading && (
            <div className="absolute inset-0 bg-accent animate-pulse flex items-center justify-center">
              <Package className="w-12 h-12 text-muted-foreground/40" />
            </div>
          )}
          <ImageComponent
            src={order.imageUrl}
            alt="订单商品"
            className={`w-full h-full object-cover transition-opacity duration-200 ${
              imgLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={() => setImgLoading(false)}
          />
        </div>

        {/* 订单信息区 */}
        <section className="bg-card rounded-lg p-4 space-y-4 shadow-sm">
          {/* 平台 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">平台</span>
            <span className="text-foreground font-medium">
              {capitalize(order.platform)}
            </span>
          </div>

          {/* 国家 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">国家</span>
            <span className="text-foreground font-medium">{order.country}</span>
          </div>

          {/* 客户名字 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">客户名字</span>
            {order.customerName ? (
              <span className="text-foreground font-medium">
                {order.customerName}
              </span>
            ) : (
              <span className="text-muted-foreground">未填写</span>
            )}
          </div>

          {/* 下单人 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">下单人</span>
            {order.orderPlacer ? (
              <span className="text-foreground font-medium">
                {order.orderPlacer}
              </span>
            ) : (
              <span className="text-muted-foreground">未填写</span>
            )}
          </div>

          {/* 下单日期 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">下单日期</span>
            {order.orderDate ? (
              <span className="text-foreground font-medium">
                {order.orderDate}
              </span>
            ) : (
              <span className="text-muted-foreground">未填写</span>
            )}
          </div>

          {/* 发货状态 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">发货状态</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isShipped
                  ? 'bg-status-success/10 text-status-success'
                  : 'bg-status-warning/10 text-status-warning'
              }`}
            >
              {isShipped ? '已发' : '未发'}
            </span>
          </div>

          {/* 运单号 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">运单号</span>
            {isShipped && order.trackingNumber ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-foreground">
                  {order.trackingNumber}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(order.trackingNumber as string)}
                  className="p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="复制运单号"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <span className="text-muted-foreground">未发货</span>
            )}
          </div>

          {/* 到库状态 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">到库状态</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isArrived
                  ? 'bg-info/10 text-info'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {isArrived ? '已到' : '未到'}
            </span>
          </div>

          {/* 是否退款 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">是否退款</span>
            {order.isRefunded ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-destructive/10 text-destructive">
                是
              </span>
            ) : (
              <span className="text-muted-foreground">否</span>
            )}
          </div>
        </section>

        {/* 删除订单 */}
        <section>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="w-full h-11 rounded-lg bg-destructive text-destructive-foreground font-medium flex items-center justify-center gap-2 hover:bg-destructive/90 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                删除订单
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>确认删除</AlertDialogTitle>
                <AlertDialogDescription>
                  确定要删除该订单吗？删除后不可恢复
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>取消</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    handleDelete();
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      删除中...
                    </>
                  ) : (
                    '删除'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </main>
    </div>
  );
};

export default OrderDetailPage;
